import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Select, Space, message, Popconfirm, Checkbox, InputNumber, Dropdown, Menu } from 'antd';
import { PlusOutlined, DeleteOutlined, InboxOutlined, RollbackOutlined, SwapOutlined, CalendarOutlined, MoreOutlined } from '@ant-design/icons';
import { useFetchWrapper } from '_helpers';
import { ROLE_GROUPS } from '_helpers/roles';
import { WEEKDAYS, formatSlots } from '_helpers/schedule';
import { ClassOverviewAttendance } from './ClassOverviewAttendance';
import { useHistory } from 'react-router-dom';

const { Option } = Select;

const userData = JSON.parse(localStorage.getItem('userData') || 'null');
const isManager = ROLE_GROUPS.MANAGERS.includes(userData?.role);

export { ManagerDashboard };

function ManagerDashboard() {
    const fetchWrapper = useFetchWrapper();
    const history = useHistory();
    const [classes, setClasses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [overviewClass, setOverviewClass] = useState(null); // class whose read-only attendance is open
    const [modalVisible, setModalVisible] = useState(false);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedTeacherId, setSelectedTeacherId] = useState(null);
    const [saving, setSaving] = useState(false);

    // Schedule (lịch học) editor state
    const [scheduleVisible, setScheduleVisible] = useState(false);
    const [scheduleClass, setScheduleClass] = useState(null);
    const [scheduleSlots, setScheduleSlots] = useState([]); // [{weekday, start, duration_min}]

    // Transfer (chuyển khoá) state
    const [transferVisible, setTransferVisible] = useState(false);
    const [transferSource, setTransferSource] = useState(null);
    const [transferMembers, setTransferMembers] = useState([]);   // [{student_id, name}]
    const [transferStudentIds, setTransferStudentIds] = useState([]);
    const [transferTargetId, setTransferTargetId] = useState(null);
    const [archiveAfter, setArchiveAfter] = useState(true);

    // Manager filter: show only classes a chosen teacher owns or is assigned to (null = all).
    const [filterTeacherId, setFilterTeacherId] = useState(null);
    const [mode, setMode] = useState('normal'); // 'normal' | 'trial' — never shown together

    const loadData = async () => {
        setLoading(true);
        try {
            const [classRes, teacherRes] = await Promise.all([
                fetchWrapper.get('/api/manager/classes', null, null),
                fetchWrapper.get('/api/manager/teachers', null, null),
            ]);
            const classJson = await classRes.json();
            const teacherJson = await teacherRes.json();
            if (classJson.status === 'Success') setClasses(classJson.message);
            if (teacherJson.status === 'Success') setTeachers(teacherJson.message);
        } catch (e) {
            message.error('Không tải được dữ liệu');
        }
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const openAssignModal = (cls) => {
        setSelectedClass(cls);
        setSelectedTeacherId(null);
        setModalVisible(true);
    };

    const handleAssign = async () => {
        if (!selectedTeacherId) { message.warning('Vui lòng chọn giáo viên'); return; }
        setSaving(true);
        try {
            var urlencoded = new URLSearchParams();
            urlencoded.append('teacher_student_id', selectedTeacherId);
            const res = await fetchWrapper.post(
                `/api/manager/classes/${selectedClass.class_id}/assign`,
                'application/x-www-form-urlencoded',
                urlencoded
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã gán giáo viên thành công');
                setModalVisible(false);
                loadData();
            } else {
                message.error(json.message || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    };

    const handleDeleteClass = async (cls) => {
        try {
            const res = await fetchWrapper.delete(`/api/manager/classes/${cls.class_id}`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã xóa lớp thành công');
                loadData();
            } else {
                message.error(json.message || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
    };

    const handleRemove = async (cls, teacherStudentId) => {
        try {
            const res = await fetchWrapper.delete(
                `/api/manager/classes/${cls.class_id}/assign/${teacherStudentId}`,
                null, null
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã xóa giáo viên khỏi lớp');
                loadData();
            } else {
                message.error(json.message || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
    };

    const handleArchive = async (cls, archived) => {
        try {
            const body = new URLSearchParams();
            body.append('archived', archived);
            const res = await fetchWrapper.put(
                `/api/manager/classes/${cls.class_id}/archive`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success(archived ? 'Đã lưu trữ lớp' : 'Đã khôi phục lớp');
                loadData();
            } else {
                message.error(json.message || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
    };

    const openSchedule = (cls) => {
        history.push(`/schedule?classId=${encodeURIComponent(cls.class_id)}`);
    };

    const setSlot = (i, patch) =>
        setScheduleSlots(slots => slots.map((s, idx) => idx === i ? { ...s, ...patch } : s));
    const addSlot = () =>
        setScheduleSlots(slots => [...slots, { weekday: 1, start: '18:00', duration_min: 90 }]);
    const removeSlot = (i) =>
        setScheduleSlots(slots => slots.filter((_, idx) => idx !== i));

    const handleSaveSchedule = async () => {
        // Mirror the server validation so the user gets a friendly message before the round-trip.
        const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
        for (const s of scheduleSlots) {
            if (!TIME_RE.test(s.start)) { message.warning('Giờ bắt đầu không hợp lệ (HH:MM)'); return; }
            if (!(s.duration_min >= 1 && s.duration_min <= 600)) { message.warning('Thời lượng 1–600 phút'); return; }
        }
        setSaving(true);
        try {
            const res = await fetchWrapper.put(
                `/api/manager/classes/${scheduleClass.class_id}/info`,
                'application/json',
                JSON.stringify({ schedule_slots: scheduleSlots })
            );
            const json = await res.json();
            if (json.status === 'Success') {
                message.success('Đã lưu lịch học');
                setScheduleVisible(false);
                loadData();
            } else {
                message.error(json.message || 'Có lỗi xảy ra');
            }
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    };

    const openTransfer = async (cls) => {
        setTransferSource(cls);
        setTransferTargetId(null);
        setArchiveAfter(true);
        setTransferMembers([]);
        setTransferStudentIds([]);
        setTransferVisible(true);
        try {
            // Source roster — student_ids needed for the add-to-target call.
            const res = await fetchWrapper.get(`/api/classes/${cls.class_id}/members/infors`, null, null);
            const json = await res.json();
            if (json.status === 'Success') {
                const members = json.message.map(m => ({ student_id: m.student_id, name: m.name }));
                setTransferMembers(members);
                setTransferStudentIds(members.map(m => m.student_id)); // default: all
            }
        } catch (e) { message.error('Không tải được danh sách học sinh'); }
    };

    const handleTransfer = async () => {
        if (!transferTargetId) { message.warning('Vui lòng chọn lớp đích'); return; }
        if (transferStudentIds.length === 0) { message.warning('Chưa chọn học sinh nào'); return; }
        setSaving(true);
        try {
            // 1. Add selected students to the target class (reuses members/add).
            const body = new URLSearchParams();
            body.append('members', JSON.stringify(transferStudentIds));
            const res = await fetchWrapper.post(
                `/api/manager/classes/${transferTargetId}/members/add`,
                'application/x-www-form-urlencoded',
                body
            );
            const json = await res.json();
            if (json.status !== 'Success') { message.error(json.message || 'Chuyển thất bại'); setSaving(false); return; }
            // 2. Archive the source class if requested.
            if (archiveAfter) await handleArchive(transferSource, true);
            message.success('Đã chuyển học sinh sang lớp mới');
            setTransferVisible(false);
            loadData();
        } catch (e) { message.error('Có lỗi xảy ra'); }
        setSaving(false);
    };

    // Confirm dialogs for the overflow menu (Popconfirm can't live inside a Menu item).
    const confirmArchive = (record) => Modal.confirm({
        title: `Lưu trữ lớp "${record.class_name}"?`,
        content: 'Học sinh sẽ không còn thấy lớp này.',
        okText: 'Lưu trữ', cancelText: 'Hủy',
        onOk: () => handleArchive(record, true),
    });
    const confirmDelete = (record) => Modal.confirm({
        title: `Xóa lớp "${record.class_name}"?`,
        okText: 'Xóa', cancelText: 'Hủy', okType: 'danger',
        onOk: () => handleDeleteClass(record),
    });

    const columns = [
        {
            title: 'Tên lớp',
            dataIndex: 'class_name',
            key: 'class_name',
            width: 180,
            render: (_, record) => (
                <div>
                    <button type="button" className="mgd-classlink" onClick={() => setOverviewClass(record)}>
                        {record.class_name}
                    </button>
                    <div className="mgd-meta">
                        {formatSlots(record.schedule_slots) || 'Chưa có lịch'}
                    </div>
                </div>
            ),
        },
        {
            title: 'Giáo viên tạo lớp',
            key: 'class_teacher',
            width: 200,
            // Managers can sort classes by owning teacher (Vietnamese-aware). Teachers see only
            // their own classes, so the sort control is pointless for them.
            sorter: isManager
                ? (a, b) => (a.class_teacher?.name || '').localeCompare(b.class_teacher?.name || '', 'vi')
                : undefined,
            render: (_, record) => (
                record.class_teacher
                    ? <span className="tag t-info">{record.class_teacher.name} ({record.class_teacher.student_id})</span>
                    : <span className="mgd-dash">—</span>
            )
        },
        {
            title: 'Giáo viên được gán',
            key: 'assigned_teachers',
            render: (_, record) => {
                const canOwn = isManager || (record.class_teacher && record.class_teacher.student_id === userData?.student_id);
                return (
                <Space wrap>
                    {record.assigned_teachers && record.assigned_teachers.length > 0
                        ? record.assigned_teachers.map(t => (
                            <span key={t.student_id} className="tag t-ok mgd-closetag">
                                {t.name} ({t.student_id})
                                {canOwn &&
                                <button
                                    type="button"
                                    className="mgd-tagclose"
                                    aria-label="Xóa giáo viên khỏi lớp"
                                    onClick={() => handleRemove(record, t.student_id)}
                                >×</button>}
                            </span>
                        ))
                        : <span className="mgd-dash">Chưa có</span>
                    }
                </Space>
                );
            }
        },
        {
            title: 'Thao tác',
            key: 'action',
            width: 180,
            render: (_, record) => {
                // Owner (creator) or manager may delete / assign teachers / archive; assigned-only
                // teachers can still edit content (schedule / transfer) but not these owner actions.
                const canOwn = isManager || (record.class_teacher && record.class_teacher.student_id === userData?.student_id);
                return record.archived ? (
                <Space wrap>
                    {isManager &&
                        <Button size="small" icon={<RollbackOutlined />} onClick={() => handleArchive(record, false)}>
                            Khôi phục
                        </Button>}
                    {canOwn &&
                    <Popconfirm
                        title={`Xóa lớp "${record.class_name}"?`}
                        onConfirm={() => handleDeleteClass(record)}
                        okText="Xóa" cancelText="Hủy" okType="danger"
                    >
                        <Button danger size="small" icon={<DeleteOutlined />}>Xóa lớp</Button>
                    </Popconfirm>}
                </Space>
            ) : (
                <Space size={8}>
                    {canOwn &&
                    <Button size="small" icon={<PlusOutlined />} onClick={() => openAssignModal(record)}>
                        Gán giáo viên
                    </Button>}
                    <Dropdown
                        trigger={['click']}
                        overlay={
                            <Menu>
                                <Menu.Item key="schedule" icon={<CalendarOutlined />} onClick={() => openSchedule(record)}>
                                    Lịch học
                                </Menu.Item>
                                <Menu.Item key="transfer" icon={<SwapOutlined />} onClick={() => openTransfer(record)}>
                                    Chuyển khoá
                                </Menu.Item>
                                {canOwn && <Menu.Item key="archive" icon={<InboxOutlined />} onClick={() => confirmArchive(record)}>
                                    Lưu trữ
                                </Menu.Item>}
                                {canOwn && <Menu.Divider />}
                                {canOwn && <Menu.Item key="delete" danger icon={<DeleteOutlined />} onClick={() => confirmDelete(record)}>
                                    Xóa lớp
                                </Menu.Item>}
                            </Menu>
                        }
                    >
                        <Button size="small" icon={<MoreOutlined />} aria-label="Thao tác khác" />
                    </Dropdown>
                </Space>
                );
            }
        }
    ];

    const matchesTeacher = (c) => !filterTeacherId ||
        (c.class_teacher && c.class_teacher.student_id === filterTeacherId) ||
        (c.assigned_teachers || []).some(t => t.student_id === filterTeacherId);
    // Lớp chính thức and lớp học thử are shown one at a time — each with its own archive, so an
    // archived trial class stays inside học thử rather than surfacing among the real classes.
    const inMode = (c) => (mode === 'trial' ? !!c.is_trial : !c.is_trial);
    const activeClasses = classes.filter(c => !c.archived && inMode(c) && matchesTeacher(c));
    const archivedClasses = classes.filter(c => c.archived && inMode(c) && matchesTeacher(c));

    const normalCount = classes.filter(c => !c.archived && !c.is_trial && matchesTeacher(c)).length;
    const trialCount = classes.filter(c => !c.archived && c.is_trial && matchesTeacher(c)).length;

    // Transfer targets are always real classes, whatever mode is showing — a cohort is never moved
    // into a lớp học thử, and reading activeClasses here would offer exactly that while in trial mode.
    const transferTargets = classes.filter(c => !c.archived && !c.is_trial && matchesTeacher(c));

    // Click a class name → read-only attendance overview (full teacher-completion + proof photos).
    if (overviewClass) {
        return (
            <ClassOverviewAttendance
                classId={overviewClass.class_id}
                className={overviewClass.class_name}
                onBack={() => setOverviewClass(null)}
            />
        );
    }

    return (
        <div className="att2-page">
            <div className="att2-head">
                <div className="att2-head-l">
                    <h1 className="att2-title">Quản lý lớp học</h1>
                    <div className="att2-sub">
                        {activeClasses.length} {mode === 'trial' ? 'lớp học thử' : 'lớp đang hoạt động'}
                    </div>
                </div>
                <div className="att2-head-r">
                    <div className="seg">
                        <button className={mode === 'normal' ? 'on' : ''} onClick={() => setMode('normal')}>
                            Chính thức <span className="seg-n">{normalCount}</span>
                        </button>
                        <button className={mode === 'trial' ? 'on' : ''} onClick={() => setMode('trial')}>
                            Học thử <span className="seg-n">{trialCount}</span>
                        </button>
                    </div>
                    {isManager &&
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            style={{ minWidth: 240 }}
                            placeholder="Lọc theo giáo viên"
                            value={filterTeacherId}
                            onChange={(v) => setFilterTeacherId(v || null)}
                        >
                            {teachers.map(t => (
                                <Option key={t.student_id} value={t.student_id}>{t.name} ({t.student_id})</Option>
                            ))}
                        </Select>}
                    </div>}
            </div>
            <Table
                columns={columns}
                dataSource={activeClasses}
                rowKey="class_id"
                loading={loading}
                scroll={{ x: 800 }}
                className="mgd-table"
            />

            {archivedClasses.length > 0 &&
                <div className="mgd-archived">
                    <h2 className="mgd-section-title">Đã lưu trữ (chỉ đọc)</h2>
                    <Table
                        columns={columns}
                        dataSource={archivedClasses}
                        rowKey="class_id"
                        pagination={false}
                        scroll={{ x: 800 }}
                        className="mgd-table"
                        rowClassName={() => 'archived-row'}
                    />
                </div>}

            <Modal
                title={`Gán giáo viên vào lớp: ${selectedClass?.class_name}`}
                visible={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleAssign}
                confirmLoading={saving}
                okText="Gán"
                cancelText="Hủy"
            >
                <p>Chọn giáo viên để gán vào lớp này:</p>
                <Select
                    style={{ width: '100%' }}
                    placeholder="Chọn giáo viên..."
                    onChange={val => setSelectedTeacherId(val)}
                    showSearch
                    filterOption={(input, option) =>
                        option.children.toLowerCase().includes(input.toLowerCase())
                    }
                >
                    {teachers.map(t => (
                        <Option key={t.student_id} value={t.student_id}>
                            {t.name} ({t.student_id}) — {t.role}
                        </Option>
                    ))}
                </Select>
            </Modal>

            <Modal
                title={`Chuyển khoá từ lớp: ${transferSource?.class_name}`}
                visible={transferVisible}
                onCancel={() => setTransferVisible(false)}
                onOk={handleTransfer}
                confirmLoading={saving}
                okText="Chuyển"
                cancelText="Hủy"
            >
                <p>Lớp đích (tạo lớp mới trước nếu cần, ở mục "Chọn lớp"):</p>
                <Select
                    style={{ width: '100%', marginBottom: 16 }}
                    placeholder="Chọn lớp đích..."
                    value={transferTargetId}
                    onChange={setTransferTargetId}
                    showSearch
                    filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                >
                    {transferTargets.filter(c => c.class_id !== transferSource?.class_id).map(c => (
                        <Option key={c.class_id} value={c.class_id}>{c.class_name}</Option>
                    ))}
                </Select>
                <p>Học sinh chuyển ({transferStudentIds.length}/{transferMembers.length}):</p>
                <Select
                    mode="multiple"
                    style={{ width: '100%', marginBottom: 16 }}
                    placeholder="Học sinh..."
                    value={transferStudentIds}
                    onChange={setTransferStudentIds}
                    optionFilterProp="children"
                >
                    {transferMembers.map(m => (
                        <Option key={m.student_id} value={m.student_id}>{m.name} ({m.student_id})</Option>
                    ))}
                </Select>
                <Checkbox checked={archiveAfter} onChange={e => setArchiveAfter(e.target.checked)}>
                    Lưu trữ lớp cũ sau khi chuyển
                </Checkbox>
            </Modal>

            <Modal
                title={`Lịch học: ${scheduleClass?.class_name}`}
                visible={scheduleVisible}
                onCancel={() => setScheduleVisible(false)}
                onOk={handleSaveSchedule}
                confirmLoading={saving}
                okText="Lưu"
                cancelText="Hủy"
            >
                <p className="mgd-hint">Mỗi buổi học lặp lại hàng tuần. Một lớp có thể có nhiều buổi.</p>
                {scheduleSlots.length === 0 && <p className="mgd-dash">Chưa có buổi nào.</p>}
                {scheduleSlots.map((s, i) => (
                    <Space key={i} className="mgd-slot-row" align="center">
                        <Select
                            style={{ width: 110 }}
                            value={s.weekday}
                            onChange={v => setSlot(i, { weekday: v })}
                        >
                            {WEEKDAYS.map(w => <Option key={w.iso} value={w.iso}>{w.label}</Option>)}
                        </Select>
                        <input
                            type="time"
                            value={s.start}
                            onChange={e => setSlot(i, { start: e.target.value })}
                            className="mgd-time-input"
                        />
                        <InputNumber
                            min={1} max={600} step={15}
                            value={s.duration_min}
                            onChange={v => setSlot(i, { duration_min: v })}
                            style={{ width: 80 }}
                        />
                        <span>phút</span>
                        <Button danger size="small" icon={<DeleteOutlined />} onClick={() => removeSlot(i)} />
                    </Space>
                ))}
                <Button icon={<PlusOutlined />} onClick={addSlot} className="mgd-add-slot">
                    Thêm buổi
                </Button>
            </Modal>
        </div>
    );
}
